/**
 * The frontier stamp: every coordinate a take was played at.
 *
 * The cell says what the take counts toward: per surface, the content (an exercise, a piece, a
 * sight-reading level, a tutorial level) and that surface's own cell axes (key and scale,
 * transposition, meter, chapter). Two takes are in the same cell when every cell field is equal.
 * Frontier coordinates are ordered and compared by meets-or-beats dominance: a take counts toward
 * any cell it meets or beats on every one of them. A take is stamped with the easiest condition
 * in force at any moment of it (its slowest tempo, the most help), so a stamp never claims more
 * than the whole take achieved.
 */

import { PRESET_CATALOG } from '../catalog';
import { TakeState } from '../grader';
import { SURFACES, Surface } from '../presetData';
import { CLOCK_DIMENSIONS, DIMENSIONS, Dimension, GradingConfigError, Hand } from '../types';

/**
 * The hands lever, per hand: you play it, the app plays it, or it is silent. Playing a hand
 * yourself meets either other role; the app playing it and silence meet only themselves.
 */
export type HandRole = 'you' | 'app' | 'silent';

export type InputMode = 'midi' | 'audio' | 'on-screen';

/** What a take actually judged: note dimensions, plus the pedal stream when it was graded. */
export type GradedDimension = Dimension | 'pedal';

/** The id of a preset in the catalog: the named configurations a take can be graded under. */
export type PresetId = string;

/** Read from the catalog, so a take can be stamped with exactly the presets it holds. */
export const PRESET_IDS: readonly PresetId[] = PRESET_CATALOG.map((preset) => preset.id);

/**
 * The assists: information shown during a take that gives away something the grader judges. The
 * click is the metronome (with its visual pulse); finger numbers are an assist only where
 * fingering is graded.
 */
export type AssistName = 'note_names' | 'lit_keys' | 'click' | 'finger_numbers';

export const ASSISTS: readonly AssistName[] = ['note_names', 'lit_keys', 'click', 'finger_numbers'];

export type AssistLevels = Readonly<Partial<Record<AssistName, number>>>;

/**
 * The scale slugs an exercise cell may name: the scale library's 80 playable scales, by the slug
 * the backend's scale table stores (one level each in the scales exercise, in the order they were
 * first written). A slug is permanent once a row names it.
 */
export const SCALES = [
  'major',
  'dorian',
  'phrygian',
  'lydian',
  'mixolydian',
  'aeolian',
  'locrian',
  'ascending-melodic-minor',
  'phrygian-nat6',
  'lydian-augmented',
  'lydian-dominant',
  'aeolian-dominant',
  'locrian-nat2',
  'superlocrian',
  'harmonic-minor',
  'locrian-nat6',
  'ionian-augmented',
  'romanian',
  'phrygian-dominant',
  'lydian-2',
  'ultralocrian',
  'harmonic-major',
  'dorian-b5',
  'phrygian-b4',
  'lydian-b3',
  'mixolydian-b2',
  'lydian-augmented-2',
  'locrian-bb7',
  'fully-diminished',
  'dominant-diminished',
  'minor-pentatonic',
  'major-pentatonic',
  'egyptian-suspended',
  'blues-minor-pentatonic',
  'blues-major-pentatonic',
  'whole-tone',
  'augmented',
  'inverted-augmented',
  'prometheus',
  'tritone',
  'hexatonic',
  'major-blues',
  'nonatonic-blues',
  'decatonic-blues',
  'double-harmonic-major',
  'lydian-2-6',
  'ultraphrygian',
  'hungarian-minor',
  'oriental',
  'ionian-2-5',
  'locrian-bb3-bb7',
  'neapolitan-major',
  'leading-whole-tone',
  'lydian-dominant-augmented',
  'lydian-minor',
  'major-locrian',
  'altered-dominant-major-2nd',
  'altered-dominant-diminished-3rd',
  'neapolitan-minor',
  'lydian-6',
  'mixolydian-augmented',
  'hungarian-gypsy',
  'locrian-dominant',
  'ionian-2',
  'ultralocrian-bb3',
  'hungarian-major',
  'ultralocrian-bb6',
  'harmonic-minor-b5',
  'superlocrian-nat6',
  'jazz-minor-5',
  'dorian-b2-4',
  'nohkan-flute-scale',
  'romanian-major',
  'super-lydian-augmentednat6',
  'locriannat2-bb7',
  'superlocrian-bb6',
  'jazz-minor-b5',
  'javanese-b4',
  'lydian-augmented-b3',
  'chromatic',
] as const;

export type Scale = (typeof SCALES)[number];

export type Tonic = 'C' | 'G' | 'D' | 'A' | 'E' | 'B' | 'F#' | 'Db' | 'Ab' | 'Eb' | 'Bb' | 'F';

export const TONICS: readonly Tonic[] = [
  'C',
  'G',
  'D',
  'A',
  'E',
  'B',
  'F#',
  'Db',
  'Ab',
  'Eb',
  'Bb',
  'F',
];

export type Direction = 'asc' | 'desc' | 'both';

export const DIRECTIONS: readonly Direction[] = ['asc', 'desc', 'both'];

export type RhythmDensity = 'quarter' | 'eighth' | 'sixteenth';

export const RHYTHM_DENSITIES: readonly RhythmDensity[] = ['quarter', 'eighth', 'sixteenth'];

/** The time-signature denominators a sight-reading cell may carry. */
export const TIME_SIGNATURE_DENOMINATORS: readonly number[] = [1, 2, 4, 8, 16, 32];

export interface ExerciseCell {
  readonly surface: 'exercise';
  readonly exerciseId: number;
  readonly scale: Scale;
  readonly tonic: Tonic;
}

export interface RepertoireCell {
  readonly surface: 'repertoire';
  readonly repertoireId: number;
  /** Semitones the piece is played transposed by, -11 to 11. */
  readonly transposeSemitones: number;
}

export interface SightReadingCell {
  readonly surface: 'sight-reading';
  readonly levelNumber: number;
  readonly timeSignatureNumerator: number;
  readonly timeSignatureDenominator: number;
}

export interface TutorialCell {
  readonly surface: 'tutorial';
  readonly levelNumber: number;
  readonly chapter: number;
}

export type Cell = ExerciseCell | RepertoireCell | SightReadingCell | TutorialCell;

/** An exercise's own frontier coordinates, compared the way the exercise progress store does. */
export interface ExerciseFrontier {
  readonly rangeOctaves: number;
  readonly direction: Direction;
  readonly rhythmDensity: RhythmDensity;
}

export interface FrontierCoordinates {
  /** The slowest tempo in force during the take; null for an untimed take. */
  readonly tempoBpm: number | null;
  readonly hands: Readonly<Record<Hand, HandRole>>;
  readonly graded: readonly GradedDimension[];
  /** The highest level of each assist in force during the take; an assist at 0 is left out. */
  readonly assists: AssistLevels;
  /** Sight reading: the tier played. */
  readonly tierNumber: number | null;
  /** Exercises only. */
  readonly rangeOctaves: number | null;
  readonly direction: Direction | null;
  readonly rhythmDensity: RhythmDensity | null;
}

/** What only the surface knows about a take, given when it begins. */
export interface StampContext {
  /** The cell; its surface is the session's. */
  readonly cell: Cell;
  readonly hands: Readonly<Record<Hand, HandRole>>;
  /** Sight reading only. */
  readonly tierNumber?: number;
  /** Exercises only. */
  readonly exercise?: ExerciseFrontier;
  readonly preset: PresetId;
  readonly input: { readonly mode: InputMode; readonly latencyMs: number | null };
  readonly goalTempoBpm?: number | null;
}

export interface TakeStamp {
  readonly cell: Cell;
  readonly frontier: FrontierCoordinates;
  readonly preset: PresetId;
  readonly input: StampContext['input'];
  readonly tempo: {
    readonly slowestBpm: number | null;
    readonly fastestBpm: number | null;
    readonly goalBpm: number | null;
  };
  readonly engineVersion: number;
  readonly offTheRecord: boolean;
}

/** The hands the player plays: the hands graded. */
export function gradedHandsOf(hands: Readonly<Record<Hand, HandRole>>): Hand[] {
  const order: readonly Hand[] = ['left', 'right'];
  return order.filter((hand) => hands[hand] === 'you');
}

/**
 * The dimensions the engine actually judged over a take: the preset's, minus the capability
 * mask and live narrowing, minus the clock dimensions when no graded note was on the clock, minus
 * duration when nothing set a tempo for it; plus the pedal stream when it was graded.
 */
export function gradedDimensionsOf(state: TakeState): GradedDimension[] {
  const graded = state.notes.filter((noteState) => noteState.graded);
  const anyOnClock =
    state.setup.clock !== null && graded.some((noteState) => noteState.rule === 'time-window');
  const anyOffClock = graded.some((noteState) => noteState.rule === 'order');
  const durationUnset = anyOffClock && !anyOnClock && state.referenceTempoBpm === null;
  const judged = DIMENSIONS.filter(
    (dimension) =>
      state.effective.dimensions[dimension] !== undefined &&
      (anyOnClock || CLOCK_DIMENSIONS.indexOf(dimension) < 0) &&
      !(dimension === 'duration' && durationUnset),
  );
  const pedalGraded =
    state.setup.judgment.pedal !== undefined &&
    (state.setup.mask?.undetectable ?? []).indexOf('pedal') < 0 &&
    state.setup.clock !== null &&
    (state.setup.score.pedalMarks ?? []).length > 0;
  return pedalGraded ? [...judged, 'pedal'] : judged;
}

function isLevel(level: number): boolean {
  return Number.isFinite(level) && level >= 0 && level <= 1;
}

/** The largest value a small integer column holds (a chapter, a tier). */
const SMALLINT_MAX = 32767;

const isIntIn = (value: unknown, low: number, high: number) =>
  Number.isInteger(value) && (value as number) >= low && (value as number) <= high;

const oneOf = <T>(value: unknown, values: readonly T[]) => values.indexOf(value as T) >= 0;

function cellErrors(cell: Cell): string[] {
  switch (cell.surface) {
    case 'exercise':
      return [
        ...(isIntIn(cell.exerciseId, 1, Number.MAX_SAFE_INTEGER)
          ? []
          : ['stamp: an exercise cell needs an exercise id']),
        ...(oneOf(cell.scale, SCALES)
          ? []
          : [`stamp: the scale is one of the ${SCALES.length} playable scales, got ${cell.scale}`]),
        ...(oneOf(cell.tonic, TONICS) ? [] : [`stamp: tonic is one of ${TONICS.join(', ')}`]),
      ];
    case 'repertoire':
      return [
        ...(isIntIn(cell.repertoireId, 1, Number.MAX_SAFE_INTEGER)
          ? []
          : ['stamp: a repertoire cell needs a repertoire id']),
        ...(isIntIn(cell.transposeSemitones, -11, 11)
          ? []
          : ['stamp: transposition is a whole number of semitones from -11 to 11']),
      ];
    case 'sight-reading':
      return [
        ...(isIntIn(cell.levelNumber, 0, Number.MAX_SAFE_INTEGER)
          ? []
          : ['stamp: a sight-reading cell needs a level number']),
        ...(isIntIn(cell.timeSignatureNumerator, 1, 32) &&
        oneOf(cell.timeSignatureDenominator, TIME_SIGNATURE_DENOMINATORS)
          ? []
          : ['stamp: a time signature is 1 to 32 over 1, 2, 4, 8, 16 or 32']),
      ];
    case 'tutorial':
      return [
        ...(isIntIn(cell.levelNumber, 0, Number.MAX_SAFE_INTEGER)
          ? []
          : ['stamp: a tutorial cell needs a level number']),
        ...(isIntIn(cell.chapter, 0, SMALLINT_MAX)
          ? []
          : ['stamp: a tutorial cell needs a chapter']),
      ];
    default:
      return [`stamp: the cell's surface is one of ${SURFACES.join(', ')}`];
  }
}

function exerciseFrontierErrors(exercise: ExerciseFrontier): string[] {
  return [
    ...(isIntIn(exercise.rangeOctaves, 1, 4) ? [] : ['stamp: an exercise range is 1 to 4 octaves']),
    ...(oneOf(exercise.direction, DIRECTIONS)
      ? []
      : [`stamp: direction is one of ${DIRECTIONS.join(', ')}`]),
    ...(oneOf(exercise.rhythmDensity, RHYTHM_DENSITIES)
      ? []
      : [`stamp: rhythm density is one of ${RHYTHM_DENSITIES.join(', ')}`]),
  ];
}

/** Every validity problem with a stamp context for a session's surface; empty when valid. */
export function stampContextErrors(context: StampContext, surface: Surface): string[] {
  return [
    ...(context.cell.surface === surface
      ? cellErrors(context.cell)
      : [`stamp: the cell is for ${context.cell.surface}, the session is ${surface}`]),
    ...(context.tierNumber === undefined ||
    (surface === 'sight-reading' && isIntIn(context.tierNumber, 1, SMALLINT_MAX))
      ? []
      : ['stamp: a tier is a whole number from 1, on sight reading only']),
    ...(context.exercise === undefined
      ? []
      : surface === 'exercise'
        ? exerciseFrontierErrors(context.exercise)
        : ['stamp: exercise coordinates belong to exercises only']),
    ...(oneOf(context.preset, PRESET_IDS)
      ? []
      : [`stamp: the preset is one of ${PRESET_IDS.join(', ')}`]),
    ...(gradedHandsOf(context.hands).length === 0 ? ['stamp: no hand is played'] : []),
  ];
}

export function validateStampContext(context: StampContext, surface: Surface): void {
  const errors = stampContextErrors(context, surface);
  if (errors.length > 0) {
    throw new GradingConfigError(errors.join('; '));
  }
}

export function validateLevel(name: string, level: number): void {
  if (!isLevel(level)) {
    throw new GradingConfigError(`${name}: a level is a number from 0 to 1, got ${level}`);
  }
}

/** Assist levels in force, keeping each assist's highest level and leaving out those at 0. */
export function mergeAssists(soFar: AssistLevels, now: AssistLevels): AssistLevels {
  return ASSISTS.reduce<Partial<Record<AssistName, number>>>((merged, name) => {
    const level = Math.max(soFar[name] ?? 0, now[name] ?? 0);
    return level > 0 ? { ...merged, [name]: level } : merged;
  }, {});
}
